// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetIsPreloadingData";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedDeliveryDate";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedPickUpDate";
import setShippingMethod from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetShippingMethod";
import translate from "@insite/client-framework/Translate";
import { CarrierDto, ShipViaDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CheckoutBox from "@steiner/App/Components/CheckoutReviewAndSubmit/CheckoutBox";
import XngLoadingOverlay from "@steiner/App/Components/Common/XngLoadingOverlay";
import loadWarehouses from "@steiner/App/Handlers/Checkout/LoadWarehouses";
import { WarehousesModel } from "@steiner/App/Services/CheckoutWarehouse";
import flatMap from "lodash/flatMap";
import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    const settingsCollection = getSettingsCollection(state);
    const { session } = state.context;
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const fulfillmentMethod = cart?.fulfillmentMethod || session.fulfillmentMethod;
    return {
        cart,
        showShippingMethod: fulfillmentMethod === FulfillmentMethod.Ship,
        showPickUpDate:
            settingsCollection.accountSettings.enableWarehousePickup &&
            settingsCollection.cartSettings.enableRequestPickUpDate &&
            fulfillmentMethod === FulfillmentMethod.PickUp,
        session,
        accountSettings: settingsCollection.accountSettings,
        cartSettings: settingsCollection.cartSettings,
        allowEmptyShipping: settingsCollection.accountSettings.allowEmptyShipping,
    };
};

const mapDispatchToProps = {
    setShippingMethod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    setIsPreloadingData,
    // XNG-Changes
    loadWarehouses,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutShippingCarrierServiceStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    noCarriersFoundGridItem?: GridItemProps;
    noCarriersFoundText?: TypographyPresentationProps;
    carrierGridItem?: GridItemProps;
    carrierSelect?: SelectPresentationProps;
    serviceGridItem?: GridItemProps;
    serviceSelect?: SelectPresentationProps;
    deliveryDateGridItem?: GridItemProps;
    deliveryDatePicker?: DatePickerPresentationProps;
    pickUpDateGridItem?: GridItemProps;
    pickUpDatePicker?: DatePickerPresentationProps;
    /**
     * XNG-Changes
     */
    boxContainer?: InjectableCss;
    branchGridItem?: GridItemProps;
    notesGridItem?: GridItemProps;
}

export const checkoutShippingCarrierServiceStyles: CheckoutShippingCarrierServiceStyles = {
    container: { gap: 20 },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    noCarriersFoundGridItem: { width: 12 },
    carrierGridItem: { width: [6, 6, 6, 3, 3] },
    serviceGridItem: { width: [6, 6, 6, 3, 3] },
    deliveryDateGridItem: { width: [12, 12, 12, 6, 6] },
    pickUpDateGridItem: { width: [12, 12, 12, 6, 6] },
};

/**
 * @deprecated Use checkoutShippingCarrierServiceStyles instead.
 */
export const checkoutShippingCarrierService = checkoutShippingCarrierServiceStyles;
const styles = checkoutShippingCarrierServiceStyles;

const CheckoutReviewAndSubmitCarrierService: FC<Props> = ({
    cart,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    showShippingMethod,
    setShippingMethod,
    setIsPreloadingData,
    allowEmptyShipping,
    loadWarehouses,
}) => {
    const [selectedCarrier, setSelectedCarrier] = useState<CarrierDto | null>(null);
    const [selectedShipVia, setSelectedShipVia] = useState<ShipViaDto | null>(null);

    /**
     * XNG-Changes
     */
    const isWillCall = selectedCarrier?.description === "Will Call";
    const [warehouses, setWarehouses] = useState<WarehousesModel["warehouses"]>([]);

    useEffect(() => {
        if (cart) {
            setSelectedCarrier(cart.carrier);
            setSelectedShipVia(cart.shipVia);
        }
    }, [cart?.carrier, cart?.shipVia]);

    const checkForWillCall = () => {
        loadWarehouses({
            onSuccess: result => {
                setWarehouses(result.warehouses || []);
            },
        });
    };

    useEffect(() => {
        checkForWillCall();
    }, []);

    if (!cart || (cart.lineCount && (!cart.cartLines || cart.cartLines.length === 0))) {
        return (
            <XngLoadingOverlay
                {...styles.centeringWrapper}
                id="checkoutShipping_carrierServiceLoading"
                loading={true}
            />
        );
    }

    /**
     * XNG-Changes
     * STNRS-431
     * A user select ship via not carrier
     * update how we select shipVia and Carrier
     */
    const carrierChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        // const selectedCarrier = cart.carriers!.find(c => c.id === event.currentTarget.value)!;
        // const defaultShipVias = selectedCarrier.shipVias!.filter(o => o.isDefault);
        // const shipVia = defaultShipVias.length > 0 ? defaultShipVias[0] : selectedCarrier.shipVias![0];

        if (!cart.carriers) {
            return;
        }

        // XNG-Changes
        const shipVias = flatMap(cart.carriers!, c => c.shipVias!);
        const shipVia = shipVias.find(s => s.id === event.currentTarget.value)!;
        const selectedCarrier = cart.carriers!.find(c => c.shipVias!.some(s => s.id === event.currentTarget.value))!;

        setIsPreloadingData({ isPreloadingData: true });
        setSelectedCarrier(selectedCarrier);
        setSelectedShipVia(shipVia);
        setShippingMethod({
            carrier: selectedCarrier,
            shipVia,
            onComplete: () => {
                setIsPreloadingData({ isPreloadingData: false });
            },
        });
    };

    /**
     * XNG-Changes
     */
    const branchChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        if (!selectedCarrier || !cart.shipVia) {
            return;
        }

        setShippingMethod({
            carrier: selectedCarrier,
            shipVia: cart.shipVia,
            ...{ willCallWarehouseId: event.currentTarget.value },
            onComplete: () => {
                setIsPreloadingData({ isPreloadingData: false });
            },
        });
    };

    const shipViaChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        if (!selectedCarrier) {
            return;
        }
        const selectedShipVia = selectedCarrier.shipVias!.find(s => s.id === event.currentTarget.value)!;
        setIsPreloadingData({ isPreloadingData: true });
        setSelectedShipVia(selectedShipVia);
        setShippingMethod({
            carrier: selectedCarrier,
            shipVia: selectedShipVia,
            onComplete: () => {
                setIsPreloadingData({ isPreloadingData: false });
            },
        });
    };

    const handleRequestPickUpDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedPickUpDate({
            requestedPickUpDate: selectedDay,
            requestedPickUpDateDisabled: selectedDayDisabled,
        });
    };
    const handleRequestDeliveryDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedDeliveryDate({
            requestedDeliveryDate: selectedDay,
            requestedDeliveryDateDisabled: selectedDayDisabled,
        });
    };

    return (
        <XngLoadingOverlay id="checkoutShipping_carrierServiceLoading" loading={false}>
            <CheckoutBox {...styles.boxContainer} title={translate("Shipping Method")}>
                <GridContainer {...styles.container}>
                    {showShippingMethod && (
                        <>
                            {cart.carriers?.length === 0 && !allowEmptyShipping && (
                                <GridItem {...styles.noCarriersFoundGridItem}>
                                    <Typography {...styles.noCarriersFoundText}>
                                        {siteMessage("ReviewAndPay_NoCarriersFound")}
                                    </Typography>
                                </GridItem>
                            )}
                            {!!cart.carriers?.length && (
                                <>
                                    <GridItem {...styles.carrierGridItem}>
                                        <Select
                                            label={translate("Select Shipping Method")}
                                            {...styles.carrierSelect}
                                            // XNG-Changes
                                            value={selectedShipVia?.id || ""}
                                            onChange={carrierChangeHandler}
                                            data-test-selector="checkoutReviewAndSubmit_carrierSelect"
                                            name="carrier"
                                            required={true}
                                        >
                                            {/* XNG-Changes */}
                                            {flatMap(cart.carriers!, c => c.shipVias!).map(s => {
                                                const id = s.id!.toString();
                                                return (
                                                    <option key={id} value={id}>
                                                        {s.description}
                                                    </option>
                                                );
                                            })}
                                        </Select>
                                    </GridItem>
                                    {isWillCall && (
                                        <GridItem {...styles.carrierGridItem}>
                                            <Select
                                                label={translate("Branch")}
                                                {...styles.carrierSelect}
                                                value={cart.properties["willCallWarehouse"] || ""}
                                                onChange={branchChangeHandler}
                                                data-test-selector="checkoutReviewAndSubmit_branchSelect"
                                                name="branch"
                                                required={true}
                                            >
                                                {warehouses!.map(c => {
                                                    const id = c.warehouseId;
                                                    return (
                                                        <option key={id} value={id}>
                                                            {c.name}
                                                        </option>
                                                    );
                                                })}
                                            </Select>
                                        </GridItem>
                                    )}
                                    {/*
                                     * XNG-Changes
                                     * By Classic code, they hide Service option
                                     */}
                                    {/* <GridItem {...styles.serviceGridItem}> */}
                                    {/*    <Select */}
                                    {/*        label={translate("Select Service")} */}
                                    {/*        {...styles.serviceSelect} */}
                                    {/*        value={selectedShipVia?.id || ""} */}
                                    {/*        onChange={shipViaChangeHandler} */}
                                    {/*        data-test-selector="checkoutReviewAndSubmit_shippingServiceSelect" */}
                                    {/*    > */}
                                    {/*        {selectedCarrier && */}
                                    {/*            selectedCarrier.shipVias!.map(s => { */}
                                    {/*                const id = s.id.toString(); */}
                                    {/*                return ( */}
                                    {/*                    <option key={id} value={id}> */}
                                    {/*                        {s.description} */}
                                    {/*                    </option> */}
                                    {/*                ); */}
                                    {/*            })} */}
                                    {/*    </Select> */}
                                    {/* </GridItem> */}
                                </>
                            )}
                        </>
                    )}
                    {/*
                     * Show PickUpDate all the time and change the label name to "Set Required Date" with min-date = new Date()
                     */}
                    {/* {showPickUpDate && ( */}
                    <>
                        <GridItem {...styles.deliveryDateGridItem} data-test-selector="checkoutSetRequiredDate">
                            <DatePicker
                                label={translate("Set Required Date")}
                                {...styles.deliveryDatePicker}
                                selectedDay={cart.requestedDeliveryDateDisplay!}
                                dateTimePickerProps={{
                                    minDate: new Date(),
                                    monthAriaLabel: translate("Required date month"),
                                    dayAriaLabel: translate("Required date day"),
                                    yearAriaLabel: translate("Required date year"),
                                    ...styles.deliveryDatePicker?.dateTimePickerProps,
                                }}
                                onDayChange={handleRequestDeliveryDateChanged}
                                data-test-selector="checkoutReviewAndSubmit_deliveryDate"
                            />
                        </GridItem>
                    </>
                    <GridItem {...styles.notesGridItem} data-test-selector="checkoutShippingInstructions">
                        {createWidgetElement("CheckoutShipping/CheckoutShippingOrderNotesEntry", {
                            fields: {},
                        })}
                    </GridItem>
                    {/* )} */}
                </GridContainer>
            </CheckoutBox>
        </XngLoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitCarrierService),
    definition: {
        displayName: "Carrier & Service",
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
    },
};

export default widgetModule;
